import React, { ReactNode } from 'react'
import { Switch } from "./ui/switch"
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip"
import { format } from 'date-fns';
import { RefreshIcon } from './ui/Icons/TooltipIcon';
import { AppContext } from '@/App';
import { useIsMobile } from '@/hooks/use-mobile';
//import { dateformat } from '@/utils/GlobalConstants';

export type PageLock = {
    id: number;
    projectId: number;
    page: number; // Use the PageName enum for type safety
    userId: string; // User who has locked the page
    userName?: string; // Optional: User name for display purposes
    lockTime: Date; // Time when the page was locked
    isLocked: boolean; // Indicates if the page is currently locked
}

interface PageLockProviderProps {
    children: ReactNode;
    pageName: PageName; // Use the PageName enum for type safety
    userId: string;
    projectId: number; // Optional: Project ID for context
    onRefreshData?: () => void; // Optional: Function to set a refresh data function, will be called when the lock is refetched
}

const PageLockProvider = ({ children, pageName, userId, projectId, onRefreshData }: PageLockProviderProps) => {
    const app = React.useContext(AppContext);
    const isMobile = useIsMobile();
    const [pageLock, setPageLock] = React.useState<PageLock | undefined>(undefined);
    const [viewOnly, setViewOnly] = React.useState<boolean>(true);
    const [lockControl, setLockControl] = React.useState<React.ReactNode>(buildLockControl());

    const refreshControl: React.ReactNode = buildRefreshControl();

    React.useEffect(() => {
        // Fetch the current page lock status when the component mounts
        getPageLock();
        setLockControl(buildLockControl());
    }, [pageName, projectId]);

    React.useEffect(() => {
        // Update the control whenever the lock state changes
        setLockControl(buildLockControl());
    }, [viewOnly, pageLock]);

    function lockPage(): Promise<PageLock | undefined> {
        const newLock: PageLock = {
            id: 0, // This should be generated or fetched from the server
            projectId: projectId, // Use the provided project ID
            userId: userId, // Replace with actual user ID logic
            userName: "", // Replace with actual user name logic
            lockTime: new Date(),
            page: pageName,
            isLocked: true,
        };

        document.body.style.cursor = 'wait';
        return fetch("Lock/", {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(newLock),
        })
            .then(response => {
                if (!response.ok) {
                    if(response.status === 409) {
                        return response.json().then(data => {
                            setPageLock(data.existingLock);
                            setViewOnly(true);
                            throw new Error(data.error);
                        });
                    }
                    throw new Error('Failed to lock the page');
                }
                return response.json();
            })
            .then(data => {
                setPageLock(data);
                setViewOnly(false);
                document.body.style.cursor = 'default';
                return data;
            })
            .catch(error => {
                app.showActionNotification("Already Locked", error.message, 'warning');
                document.body.style.cursor = 'default';
                return undefined;
            });
    }

    function unlockPage(lockId?: number) {
        const idToUnlock = lockId || pageLock?.id;
        
        // If there's no page lock, just set view only and return
        if (!idToUnlock) {
            setViewOnly(true);
            return;
        }
        
        document.body.style.cursor = 'wait';
        fetch(`Lock/Unlock/${idToUnlock}`, {
            method: 'PUT',
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to unlock the page');
                }
                setPageLock(undefined);
                setViewOnly(true); // you no longer have the lock 
                document.body.style.cursor = 'default';
            })
            .catch(error => {
                console.error('Error unlocking page:', error);
                document.body.style.cursor = 'default';
            });
    }

    function getPageLock(): PageLock | undefined {
        let newPageLock: PageLock | undefined = undefined;
        document.body.style.cursor = 'wait';
        fetch(`Lock/${pageName}/${projectId}`)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch page lock');
                }
                return response.json();
            })
            .then(data => {
                if (data) {
                    setPageLock(data.isLocked ? data : undefined);
                    setViewOnly(!(data.isLocked && data.userId === userId)); // if its not locked for you, then view only
                    newPageLock = data;
                } else {
                    setPageLock(undefined);
                    setViewOnly(true);
                }
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            })
            .catch(error => {
                console.error('Error fetching page lock:', error);
                document.body.style.cursor = 'default';
            });

        return newPageLock;
    }

    function buildLockControl() {
        const checked = pageLock ? pageLock.isLocked && pageLock.userId === userId : false;
        const disabled = pageLock ? pageLock.isLocked && pageLock.userId !== userId : false;
        return (
            <div className={`flex flex-row items-center gap-2 ${isMobile ? 'text-xl' : ''}`}>
                {pageLock ? <div>Locked by {pageLock.userName} - {format(pageLock.lockTime, "dd-MMM-yy HH:mm")}</div> : <div>View Only</div>}
                <TooltipProvider delayDuration={0}>
                    <UITooltip>
                        <TooltipTrigger asChild>
                            <div>
                                <Switch className={`data-[state=checked]:bg-accent data-[state=unchecked]:bg-slate-500`}
                                    id="pageLockSwitch"
                                    checked={checked}
                                    disabled={disabled}
                                    onCheckedChange={(checked) => checked ? lockPage() : unlockPage()}
                                />
                            </div>
                        </TooltipTrigger>
                        <TooltipContent className={`bg-black text-white ${isMobile ? 'text-xl' : ''}`}>
                            {checked ? <span>Unlock Page</span> : <span className="">Lock Page for Editing</span>}
                        </TooltipContent>
                    </UITooltip>
                </TooltipProvider>
            </div>
        );
    }

    function buildRefreshControl() {
        return (
            <div className='flex flex-col items-center'>
                <button
                    className="text-accent p-1 my-auto mx-2 rounded-full hover:bg-button-background hover:text-button-text transition-colors"
                    id='refreshPageLock'
                    onClick={() => {
                        getPageLock();
                        if (onRefreshData) {
                            onRefreshData();
                        }
                    }}
                >
                    <RefreshIcon className={`w-4 h-4 ${isMobile ? 'w-7 h-7' : ''}`} />
                </button>
            </div>
        );
    }

    function getRefreshControl(): React.ReactNode {
        return (refreshControl);
    }

    function getLockControl(): React.ReactNode {
        return (lockControl);
    }

    return (
        <PageLockContext.Provider value={{ lockPage, unlockPage, getPageLock, getLockControl: getLockControl, getRefreshControl, viewOnly}}>
            {children}
        </PageLockContext.Provider>
    )
}

export default PageLockProvider

interface PageLockContextProps {
    lockPage: () => Promise<PageLock | undefined>;
    unlockPage: (lockId?: number) => void;
    getPageLock: () => PageLock | undefined;
    getLockControl: () => React.ReactNode;
    getRefreshControl: () => React.ReactNode;
    viewOnly: boolean;
}

export const PageLockContext = React.createContext<PageLockContextProps>({
    lockPage: () => Promise.resolve(undefined),
    unlockPage: () => { },
    getPageLock: () => undefined,
    getLockControl: () => <></>,
    getRefreshControl: () => <></>,
    viewOnly: false,
});

export enum PageName {
    Plan = 1,
    Charter = 2,
    StatusReport = 3,
    Risks = 4,
    Issues = 5,
    Actions = 6,
    Decisions = 7,
    SupportRequests = 8,
}